// src/components/lessons/renderers/ScenarioRenderer.jsx
import React, { useState } from 'react';
import { MessageCircle } from 'lucide-react';

const ScenarioRenderer = ({ block, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        background: '#FFFFFF',
        borderRadius: '0.75rem',
        boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '2px solid #000000',
        padding: isMobile ? '1.25rem' : '1.5rem',
        marginBottom: '1.5rem',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Blue accent bar at top */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: '#3A86FF'
      }} />

      {/* Title */}
      {block.title && (
        <h4 style={{
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          fontWeight: '700',
          color: '#000000',
          marginBottom: '1.25rem',
          marginTop: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: '#000000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.3s ease',
            transform: isHovered ? 'rotate(12deg) scale(1.1)' : 'rotate(0deg) scale(1)'
          }}>
            <MessageCircle size={16} style={{ color: '#3A86FF' }} />
          </div>
          {block.title}
        </h4>
      )}

      {/* Quote */}
      {block.quote && (
        <blockquote style={{
          fontSize: isMobile ? '1rem' : '1.25rem',
          fontStyle: 'italic',
          fontWeight: '600',
          textAlign: 'center',
          color: '#000000',
          padding: isMobile ? '1.25rem' : '1.5rem',
          background: '#FAFBFC',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          border: '2px solid #000000',
          borderLeft: '4px solid #3A86FF',
          position: 'relative'
        }}>
          <span style={{
            fontSize: '2rem',
            position: 'absolute',
            top: '0.5rem',
            left: '0.75rem',
            color: '#3A86FF',
            opacity: 0.3
          }}>
            "
          </span>
          {block.quote}
          <span style={{
            fontSize: '2rem',
            position: 'absolute',
            bottom: '0.5rem',
            right: '0.75rem',
            color: '#3A86FF',
            opacity: 0.3
          }}>
            "
          </span>
        </blockquote>
      )}

      {/* Preparation Map */}
      {block.prep_map && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h5 style={{
            fontSize: isMobile ? '0.95rem' : '1rem',
            fontWeight: '700',
            color: '#000000',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#3A86FF'
            }} />
            Preparation Map:
          </h5>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '0.75rem',
            fontSize: isMobile ? '0.85rem' : '0.9rem'
          }}>
            {Object.entries(block.prep_map).map(([key, value]) => (
              <div key={key} style={{
                background: '#FAFBFC',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid #E5E7EB',
                borderLeft: '3px solid #3A86FF'
              }}>
                <span style={{
                  fontWeight: '700',
                  textTransform: 'capitalize',
                  color: '#000000',
                  display: 'block',
                  marginBottom: '0.5rem'
                }}>
                  {key}:
                </span>
                <p style={{
                  color: '#000000',
                  margin: 0,
                  opacity: 0.8,
                  lineHeight: '1.5'
                }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partner Questions */}
      {block.partner_questions && (
        <div>
          <h5 style={{
            fontSize: isMobile ? '0.95rem' : '1rem',
            fontWeight: '700',
            color: '#000000',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#3A86FF'
            }} />
            Partner Questions:
          </h5>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}>
            {block.partner_questions.map((q, i) => (
              <li key={i} style={{
                color: '#000000',
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                lineHeight: '1.6',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '0.75rem',
                background: '#FAFBFC',
                borderRadius: '0.5rem',
                border: '1px solid #E5E7EB',
                opacity: 0.9
              }}>
                <span style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#3A86FF',
                  marginTop: '0.5rem',
                  flexShrink: 0
                }} />
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Decorative corner dot */}
      {isHovered && (
        <div style={{
          position: 'absolute',
          bottom: '12px',
          right: '12px',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: '#3A86FF',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      )}
    </div>
  );
};

export default ScenarioRenderer;